// Profil-Nachbau F-16 (Bennys Freigabe 14.10.2026, BAU-RECHT.md 2.2, PF-781): die Wörter der Seite „Verträge & AVV“, des
// AVV-Abschnitts beim Einzelkonto und der Unterauftragsverarbeiter, wörtlich nach SPEC-WELLE-A WA-10 bis WA-15 mit den
// Ergänzungen aus BAU-RECHT 2.2. Der Produktname kommt vom Aufrufer (brand.displayName, MK-1). Die Sperre der Recherche ohne AVV
// gilt erst ab einem Datum, das der Anbieter setzt (F-16): Nur wenn der Server dieses Datum nennt (erforderlich), steht ein Satz
// über die Recherche da; sonst behauptet kein Text eine Sperre.
// Geprüft in frontend/tests/recht-avv.test.mjs.
import type { ErklaerInhalt } from '../../components/Erklaerung';
import { bereichsTitel, ORGANISATION_PFAD } from '../../pages/einstellungen/seiten';
import { tagText } from '../billing/shared';
import type { AvvAntwort, AvvNachweisZeile, AvvStand, UavAenderung, UavAufgabe, UavEintrag, UavGeplant } from './rechtApi';

/** Die Adresse der Seite „Verträge & AVV“ der Firma; die Leiste und die Route trägt PF-786 ein. */
export const VERTRAEGE_PFAD = `${ORGANISATION_PFAD}/vertraege`;

// F-24 (SPEC-recht-b 3.1): die drei Schritte der Annahme an einer Stelle; wieSchritte setzt die Vorsilben davor, das Fenster wie
// zeigt sie ohne.
const SCHRITTE = {
  lesen: 'Laden Sie die aktuelle Fassung als PDF herunter.',
  annehmen: 'Die Besitzerin, der Besitzer oder wer die Abrechnung ändern darf, nimmt den AVV für die Firma an. Gespeichert werden Fassung, Prüfsumme des Dokuments, Zeitpunkt, Person und die Liste der Unterauftragsverarbeiter.',
  hochladen: (produkt: string) => `Laden Sie den unterschriebenen AVV als PDF hoch. Der Anbieter von ${produkt} prüft ihn und bestätigt ihn hier.`,
};

export const AVV_TEXTE = {
  seite: 'Verträge & AVV',
  laden: 'Verträge werden geladen …',
  fehler: 'Die Angaben zum AVV konnten nicht geladen werden.',
  erneut: 'Erneut versuchen',
  // WA-10
  titel: 'Auftragsverarbeitung (AVV)',
  beschreibung: (produkt: string) => `Der Auftragsverarbeitungsvertrag regelt, wie ${produkt} personenbezogene Daten im Auftrag Ihrer Firma verarbeitet (Art. 28 DSGVO).`,
  fassung: (version: string) => `Fassung ${version}`,
  aktuelleFassung: 'Aktuelle Fassung',
  veroeffentlicht: (tag: string) => `Veröffentlicht am ${tag}`,
  anzahlUav: (n: number) => `${n} Unterauftragsverarbeiter`,
  pdfHerunterladen: 'Als PDF herunterladen',
  stand: 'Stand',
  angenommenVon: (tag: string, person: string) => `Angenommen am ${tag} von ${person}`,
  fruehere: 'einer früheren Person',
  bestaetigtAm: (tag: string) => `Unterschriebener AVV bestätigt am ${tag}`,
  wirdGeprueft: 'Unterschriebener AVV wird geprüft',
  alteFassung: (alt: string, neu: string) => `Fassung ${alt} angenommen, Fassung ${neu} noch nicht`,
  nichtAngenommen: 'Noch nicht angenommen',
  erforderlich: (tag: string) => `Erforderlich für: Recherche in Ihrer Firma, ab ${tag}. Ohne Annahme kann ab diesem Tag niemand in der Firma neu recherchieren; gespeicherte Verläufe bleiben lesbar.`,
  // BAU-RECHT 2.2: Ausnahme und abgelehnter Nachweis.
  ausnahmeBis: (tag: string) => `Für Ihre Firma gilt eine Ausnahme bis ${tag}: Neue Recherchen bleiben bis dahin auch ohne Annahme möglich.`,
  ausnahmeUnbefristet: 'Für Ihre Firma gilt eine unbefristete Ausnahme: Neue Recherchen bleiben auch ohne Annahme möglich.',
  nachweisAbgelehnt: (tag: string, grund: string) => `Ihr hochgeladener AVV vom ${tag} wurde abgelehnt: ${grund}`,
  annehmen: 'AVV annehmen …',
  hochladen: 'Unterschriebenen AVV hochladen …',
  ohneRecht: (namen: readonly string[]) => namen.length
    ? `Annehmen kann die Besitzerin oder der Besitzer und wer die Abrechnung ändern darf: ${namen.join(', ')}.`
    : 'Annehmen kann die Besitzerin oder der Besitzer und wer die Abrechnung ändern darf.',
  wieTitel: 'Wie funktioniert die Annahme?',
  wieSchritte: (produkt: string) => [`Lesen: ${SCHRITTE.lesen}`, `Annehmen: ${SCHRITTE.annehmen}`, `Oder: ${SCHRITTE.hochladen(produkt)}`],
  wieNeueFassung: 'Eine neue Fassung wird eigens angeboten und ersetzt keinen bestehenden Nachweis.',
  // F-24 (SPEC-recht-b 3.1, F1): „Wie funktioniert die Annahme?“ als Fenster rechts im Abschnittskopf wie meinGPT „Wie funktioniert
  // die Unterzeichnung?“; Teil 1 ist der bisherige Satz unter dem Titel, die Teile 2 bis 4 die Schritte ohne Vorsilbe.
  wie: (produkt: string): ErklaerInhalt => ({
    frage: AVV_TEXTE.wieTitel,
    teile: [
      { titel: '1. Was der AVV regelt', text: AVV_TEXTE.beschreibung(produkt) },
      { titel: '2. Lesen', text: SCHRITTE.lesen },
      { titel: '3. Annehmen', text: SCHRITTE.annehmen },
      { titel: '4. Oder hochladen', text: SCHRITTE.hochladen(produkt) },
    ],
    fuss: AVV_TEXTE.wieNeueFassung,
    hilfe: 'avv',
  }),
  nachweiseTitel: (n: number) => `Bisherige Nachweise (${n})`,
  nachweiseSpalten: { datum: 'Datum', fassung: 'Fassung', art: 'Art', person: 'Person', stand: 'Stand', dokument: 'Dokument' },
  nachweisArt: { KLICK: 'Annahme per Klick', NACHWEIS: 'Unterschriebenes PDF' },
  nachweisAngenommen: 'Angenommen',
  nachweisWirdGeprueft: 'Wird geprüft',
  nachweisBestaetigt: (tag: string) => `Bestätigt am ${tag}`,
  nachweisAbgelehntAm: (tag: string, grund: string) => `Abgelehnt am ${tag}: ${grund}`,
  pdf: 'PDF',
  keineNachweise: 'Noch keine Nachweise.',
  keineFassung: (produkt: string) => `Der Anbieter von ${produkt} hat noch keine AVV-Fassung veröffentlicht.`,
  avVertrag: 'AVV',
  intern: 'Für die interne Organisation ist kein AVV nötig.',
  // WA-11
  annahmeTitel: (version: string) => `AVV in der Fassung ${version} annehmen?`,
  annahmeListe: (firma: string, version: string, produkt: string) => [
    `Sie schließen den Auftragsverarbeitungsvertrag für ${firma} mit dem Anbieter von ${produkt} ab.`,
    `Gespeichert werden Fassung ${version}, die Prüfsumme des Dokuments, der Zeitpunkt, Ihr Name und die Liste der Unterauftragsverarbeiter von heute.`,
    'Die Annahme steht im Änderungsprotokoll der Firma. Eine neue Fassung wird eigens angeboten.',
  ],
  annahmeHaken: (firma: string) => `Ich habe den AVV gelesen und darf ihn für ${firma} abschließen.`,
  abbrechen: 'Abbrechen',
  annehmenKnopf: 'AVV annehmen',
  angenommen: (firma: string) => `Der AVV ist für ${firma} angenommen.`,
  // Ohne bekannten Firmennamen (etwa ohne Profil der Firma) steht „Ihre Firma“ an seiner Stelle.
  ihreFirma: 'Ihre Firma',
  // WA-12
  hochladenTitel: 'Unterschriebenen AVV hochladen',
  fassungFeld: 'Fassung',
  dateiFeld: 'PDF-Datei (höchstens 5 MB)',
  hochladenSatz: (produkt: string) => `Der Anbieter von ${produkt} prüft das Dokument. Bis zur Bestätigung gilt der AVV als nicht angenommen.`,
  hochladenKnopf: 'Hochladen',
  dateiFehlt: 'Bitte eine PDF-Datei wählen.',
  dateiZuGross: 'Die Datei ist größer als 5 MB.',
  keinPdf: 'Der Dateiinhalt ist kein PDF.',
  hochgeladen: (produkt: string) => `Hochgeladen. Der Anbieter von ${produkt} prüft das Dokument.`,
  // „Rechnungen und Vertrag“ der Firma (BAU-RECHT 2.2)
  linkzeile: 'Auftragsverarbeitungsvertrag (AVV)',
  // WA-15 und BAU-RECHT 2.2
  datenschutzTitel: 'Datenschutz und Sicherheit',
  datenschutzBeschreibung: 'Technische Dokumentation und Nachweise sind unabhängig vom Vertrag abrufbar.',
  doku: 'Dokumentation zum Datenschutz',
  dokuErklaerung: (produkt: string) => `Wie ${produkt} personenbezogene Daten verarbeitet, speichert und schützt.`,
  nachweise: 'Nachweise',
  nachweiseErklaerung: 'Zertifizierungen, Sicherheitsmaßnahmen und Prüfberichte.',
  vertragTitel: 'Vertragsbedingungen',
  agb: 'Vertragsbedingungen (AGB)',
  datenschutzerklaerung: 'Datenschutzerklärung',
  fassungX: (fassung: string) => `Fassung ${fassung}`,
  vertragSatz: () => `Neue Fassungen nehmen Sie unter „${bereichsTitel('billing')}“ an.`,
} as const;

/** WA-14 und BAU-RECHT 2.2: Unterauftragsverarbeiter, geplante Änderung und Widerspruch. */
export const UAV_TEXTE = {
  titel: 'Unterauftragsverarbeiter',
  satz: 'Diese Dienste können im Auftrag Ihrer Firma Daten verarbeiten. Welche davon an einer Recherche beteiligt sind, hängt von den genutzten Funktionen ab.',
  // F-24 (SPEC-recht-b 3.1, U1, T1): Unterzeile und Tooltip des Abschnitts; der Dialog „Unterauftragsverarbeiter“ zeigt satz weiter ganz.
  unterzeile: 'Dienste, die im Auftrag Ihrer Firma Daten verarbeiten können',
  tipp: 'Welche Dienste an einer Recherche beteiligt sind, hängt von den genutzten Funktionen ab.',
  spalten: { name: 'Name', zweck: 'Zweck', region: 'Region', website: 'Website' },
  website: 'Website',
  leer: 'Der Anbieter hat noch keine Unterauftragsverarbeiter eingetragen.',
  schliessen: 'Schließen',
  laden: 'Unterauftragsverarbeiter werden geladen …',
  geplant: (tag: string, z: AenderungZahlen) => `Ab ${tag} ändert sich die Liste: ${z.neu} neu, ${z.geaendert} geändert, ${z.entfallen} entfallen. Bis dahin können Sie widersprechen.`,
  neuAb: (tag: string) => `neu ab ${tag}`,
  entfaelltAb: (tag: string) => `entfällt ab ${tag}`,
  geaendertAb: (tag: string) => `geändert ab ${tag}`,
  widersprechen: 'Widerspruch einlegen …',
  widerspruchTitel: 'Widerspruch gegen die geplante Änderung',
  widerspruchSatz: (produkt: string) => `Ihr Widerspruch geht an den Anbieter von ${produkt}. Er antwortet Ihnen hier und per E-Mail.`,
  begruendung: 'Begründung',
  begruendungHinweis: 'Bitte keine Mandantendaten eintragen.',
  begruendungKurz: 'Bitte mindestens 10 Zeichen eintragen.',
  senden: 'Widerspruch senden',
  eingegangen: 'Ihr Widerspruch ist eingegangen.',
  eingelegt: (tag: string) => `Widerspruch eingelegt am ${tag}`,
  antwort: (tag: string, text: string) => `Antwort des Anbieters vom ${tag}: ${text}`,
} as const;

/** Die Gruppen in fester Reihenfolge (WA-14). */
export const AUFGABEN: readonly { aufgabe: UavAufgabe; titel: string }[] = [
  { aufgabe: 'HOSTING', titel: 'Rechenzentrum und Hosting' },
  { aufgabe: 'KI', titel: 'KI-Dienste' },
  { aufgabe: 'WEBSUCHE', titel: 'Websuche' },
  { aufgabe: 'ZAHLUNG', titel: 'Zahlung' },
  { aufgabe: 'EMAIL', titel: 'E-Mail-Versand' },
  { aufgabe: 'SUPPORT', titel: 'Support' },
  { aufgabe: 'SONSTIGES', titel: 'Sonstiges' },
];

export const BEGRUENDUNG_MIN = 10;
export const BEGRUENDUNG_MAX = 1000;
export const DATEI_MAX_BYTES = 5 * 1024 * 1024;

export type MarkenTon = 'positiv' | 'neutral';
/** Die eine Marke des Stands (WA-10): genau einer der fünf Fälle. Ohne Namen (Person gelöscht) „von einer früheren Person“. */
export function standMarke(stand: AvvStand | null, aktuelleVersion?: string | null): { text: string; ton: MarkenTon } {
  if (!stand) return { text: AVV_TEXTE.nichtAngenommen, ton: 'neutral' };
  switch (stand.art) {
    case 'KLICK': return { text: AVV_TEXTE.angenommenVon(tagText(stand.am), stand.personName || AVV_TEXTE.fruehere), ton: 'positiv' };
    case 'NACHWEIS_BESTAETIGT': return { text: AVV_TEXTE.bestaetigtAm(tagText(stand.am)), ton: 'positiv' };
    case 'NACHWEIS_OFFEN': return { text: AVV_TEXTE.wirdGeprueft, ton: 'neutral' };
    case 'ALTE_FASSUNG': return { text: AVV_TEXTE.alteFassung(stand.fassungVersion, aktuelleVersion ?? ''), ton: 'neutral' };
    default: return { text: AVV_TEXTE.nichtAngenommen, ton: 'neutral' };
  }
}

/** Die Spalte „Stand“ der bisherigen Nachweise (WA-10). */
export function nachweisStand(zeile: Pick<AvvNachweisZeile, 'status' | 'entschiedenAm' | 'grund'>): string {
  const tag = zeile.entschiedenAm ? tagText(zeile.entschiedenAm) : '–';
  switch (zeile.status) {
    case 'EINGEREICHT': return AVV_TEXTE.nachweisWirdGeprueft;
    case 'BESTAETIGT': return AVV_TEXTE.nachweisBestaetigt(tag);
    case 'ABGELEHNT': return AVV_TEXTE.nachweisAbgelehntAm(tag, zeile.grund ?? '');
    default: return AVV_TEXTE.nachweisAngenommen;
  }
}

/** Ob der Stand eine gültige Annahme nennt (für die Einrichtungsliste, WA-17). Der Server sagt es mit gueltig; ohne das Feld
 *  zählt nur eine Annahme der aktuellen Fassung, per Klick oder als bestätigter Nachweis. */
export const gueltigAngenommen = (stand: AvvStand | null): boolean =>
  !!stand && (stand.gueltig ?? (stand.art === 'KLICK' || stand.art === 'NACHWEIS_BESTAETIGT'));

/** Die offenen Sätze unter dem Stand, in dieser Reihenfolge: „Erforderlich für …“ nur mit gesetztem „verbindlich ab“ (WA-13),
 *  danach eine aktive Ausnahme (BAU-RECHT 2.2). Ohne beides keine Zeile: Dann sperrt nichts, und kein Satz sagt etwas anderes. */
export function erforderlichSatz(erforderlich: AvvAntwort['erforderlich'], ausnahme: AvvAntwort['ausnahme']): string[] {
  return [
    ...(erforderlich ? [AVV_TEXTE.erforderlich(tagText(erforderlich.ab))] : []),
    ...(ausnahme ? [ausnahme.bis ? AVV_TEXTE.ausnahmeBis(tagText(ausnahme.bis)) : AVV_TEXTE.ausnahmeUnbefristet] : []),
  ];
}

export type AenderungZahlen = { neu: number; geaendert: number; entfallen: number };
/** „{a} neu, {b} geändert, {c} entfallen“ aus dem Abbild der Ankündigung. */
export function aenderungZahlen(geplant: Pick<UavGeplant, 'aenderungen'>): AenderungZahlen {
  const zahl = (art: UavAenderung['art']) => geplant.aenderungen.filter(a => a.art === art).length;
  return { neu: zahl('NEU'), geaendert: zahl('GEAENDERT'), entfallen: zahl('ENTFAELLT') };
}

/** Eine Zeile der Tabelle: ein gültiger Eintrag oder ein angekündigter neuer, mit Marke bei geplanter Änderung. */
export type UavZeile = { key: string; name: string; aufgabe: UavAufgabe; zweck: string; region: string; website: string | null; marke: string | null };
/** Die Zeilen der Liste: die gültigen Einträge, je mit „geändert ab“ bzw. „entfällt ab“, wenn die Ankündigung sie nennt, dazu die
 *  angekündigten neuen mit „neu ab“. */
export function uavZeilen(gueltig: readonly UavEintrag[], geplant: UavGeplant | null): UavZeile[] {
  const tag = geplant ? tagText(geplant.wirksamAb) : '';
  const aenderung = (name: string) => geplant?.aenderungen.find(a => a.name === name && a.art !== 'NEU');
  return [
    ...gueltig.map(e => {
      const a = aenderung(e.name);
      return { key: e.id, name: e.name, aufgabe: e.aufgabe, zweck: e.zweck, region: e.region, website: e.website,
        marke: !a ? null : a.art === 'ENTFAELLT' ? UAV_TEXTE.entfaelltAb(tag) : UAV_TEXTE.geaendertAb(tag) };
    }),
    ...(geplant?.aenderungen ?? []).filter(a => a.art === 'NEU').map((a, i) => ({ key: `neu-${i}-${a.name}`, name: a.name, aufgabe: a.aufgabe, zweck: a.zweck,
      region: a.region, website: null, marke: UAV_TEXTE.neuAb(tag) })),
  ];
}

/** Die Gruppen nach Aufgabe in fester Reihenfolge; leere Gruppen entfallen. */
export function aufgabenGruppen<T extends { aufgabe: UavAufgabe }>(eintraege: readonly T[]): { aufgabe: UavAufgabe; titel: string; eintraege: T[] }[] {
  return AUFGABEN.map(({ aufgabe, titel }) => ({ aufgabe, titel, eintraege: eintraege.filter(e => e.aufgabe === aufgabe) })).filter(g => g.eintraege.length > 0);
}

/** Nur eine https-Adresse wird ein Link (WA-14). */
export function websiteAdresse(website: string | null | undefined): string | null {
  if (!website) return null;
  try { const url = new URL(website.trim()); return url.protocol === 'https:' ? url.href : null; } catch { return null; }
}

/** Die Prüfung der Datei vor dem Hochladen (WA-12); der Server prüft dasselbe noch einmal. */
export function dateiFehler(datei: { name: string; type: string; size: number } | null): string | null {
  if (!datei || !(datei.type === 'application/pdf' || /\.pdf$/i.test(datei.name))) return AVV_TEXTE.dateiFehlt;
  return datei.size > DATEI_MAX_BYTES ? AVV_TEXTE.dateiZuGross : null;
}
/** Ein PDF beginnt mit „%PDF-“, als Base64 „JVBERi0“. */
export const istPdfInhalt = (base64: string) => base64.startsWith('JVBERi0');
